import { useSenderAccountContext } from "context/SenderAccountContext";
import { DOCS_FORM_NAMES } from "core/consts";
import { getIsDtaActive } from "core/model/productfeatures/utils";
import {
  Auction,
  DischargeLetterCardiologyFormData,
  DischargeLetterENTFormData,
  DocsFormName,
  GetOntologyType,
  GKVGeneralFormData,
  GKVMedicalFormData,
  Patient,
  RehabForms,
  TransitionalCareFormData,
  ValueOf,
} from "core/types";
import { DRVGeneralFormData } from "dsl/ecosystems/PatientForms/RehabForms/DRVGeneralForm/modelDefinition";
import { DRVMedicalFormData } from "dsl/ecosystems/PatientForms/RehabForms/DRVMedicalForm/modelDefinition";
import { ReactNode } from "react";
import { DefaultValues } from "react-hook-form";
import Translations from "translations/types";
import { z } from "zod";
import { SourceInformation } from "..";
import { getDischargeLetterCardiologyFormConfig } from "./DischargeLetters/Cardiology";
import { getDischargeLetterENTFormConfig } from "./DischargeLetters/ENT";
import { getDRVGeneralFormConfig } from "./DRVGeneral";
import { getDRVMedicalFormConfig } from "./DRVMedical";
import { getGKVGeneralFormConfig } from "./GKVGeneral";
import { getGKVMedicalFormConfig } from "./GKVMedical";
import { getTransitionalCareFormConfig } from "./TransitionalCare";

type DocsFormTypeMapping = {
  [DOCS_FORM_NAMES.DRV_MEDICAL]: DRVMedicalFormData;
  [DOCS_FORM_NAMES.DRV_GENERAL]: DRVGeneralFormData;
  [DOCS_FORM_NAMES.GKV_MEDICAL]: GKVMedicalFormData;
  [DOCS_FORM_NAMES.GKV_GENERAL]: GKVGeneralFormData;
  [DOCS_FORM_NAMES.TRANSITIONAL_CARE]: TransitionalCareFormData;
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT]: DischargeLetterENTFormData;
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY]: DischargeLetterCardiologyFormData;
};

export type DocsFormDataMapped<T extends DocsFormName> = DocsFormTypeMapping[T];

export type DocsFormFormGetters<T extends DocsFormName> = Partial<
  Record<
    keyof DocsFormDataMapped<T>,
    (auction: Auction) => ValueOf<DocsFormDataMapped<T>> | null
  >
>;

export type FormFieldConfigProps<T extends DocsFormName> = {
  auction?: Auction;
  formName: T;
  skipRequiredValidation?: boolean;
  translations: Translations;
  sourceInformation: SourceInformation<T>;
};

export type FormFieldConfig<T extends DocsFormName> = {
  schema: z.ZodType<DocsFormDataMapped<T>>;
  header: (props: { translations: Translations }) => string;
  title: (props: { translations: Translations }) => string;
  config: { canPrint: boolean; canSendToHis: boolean };
  auctionToForm: (
    getOntology: GetOntologyType,
    translations: Translations,
  ) => DocsFormFormGetters<T>;
  getInitialValue: (props: {
    rehabForms: RehabForms | null | undefined;
    patient: Patient;
    value?: DocsFormDataMapped<T> | null | undefined;
  }) => DefaultValues<DocsFormDataMapped<T>>;
  sections: Array<{
    position: number;
    title?: (props: { translations: Translations }) => string;
    description?: (props: { translations: Translations }) => string;
    subTitle?: (props: { translations: Translations }) => string;
    fields: Partial<
      Record<
        keyof DocsFormDataMapped<T>,
        {
          subTitle?: (props: { translations: Translations }) => string;
          description?: (props: { translations: Translations }) => string;
          input: (props: FormFieldConfigProps<T>) => ReactNode;
          position: number;
          hideSource?: boolean;
        }
      >
    >;
  }>;
};

export type FormFieldConfigFactory<T extends DocsFormName> = (args: {
  translations: Translations;
  skipRequiredValidation: boolean;
}) => FormFieldConfig<T>;

type FormFieldConfigRegistry = {
  [K in DocsFormName]: FormFieldConfigFactory<K>;
};

const FORM_FIELD_CONFIGS: FormFieldConfigRegistry = {
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY]:
    getDischargeLetterCardiologyFormConfig,
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT]: getDischargeLetterENTFormConfig,
  [DOCS_FORM_NAMES.DRV_GENERAL]: getDRVGeneralFormConfig,
  [DOCS_FORM_NAMES.DRV_MEDICAL]: getDRVMedicalFormConfig,
  [DOCS_FORM_NAMES.GKV_GENERAL]: getGKVGeneralFormConfig,
  [DOCS_FORM_NAMES.GKV_MEDICAL]: getGKVMedicalFormConfig,
  [DOCS_FORM_NAMES.TRANSITIONAL_CARE]: getTransitionalCareFormConfig,
};

export function useGetFormFieldConfig<T extends DocsFormName>(
  formName: T,
  translations: Translations,
) {
  const { careseeker } = useSenderAccountContext();
  const isDtaActivated = getIsDtaActive({ careseeker });

  const skipRequiredValidation =
    !isDtaActivated || formName !== DOCS_FORM_NAMES.GKV_GENERAL;

  const factory = FORM_FIELD_CONFIGS[formName];

  return {
    config: factory({ skipRequiredValidation, translations }),
    skipRequiredValidation,
  } as const;
}
